import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraInput, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { pricingApiConnect } from '../../../../lib/api/pricingApi.connect';
import { projectDataClient } from '../../../../lib/connect/clients';
import { pricingHref } from './pricingRoutes';
import { money } from './ratesView';
import { usePricingRateBook, usePricingScenarios, usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
function FrozenVersion({ scope }) {
    const evidence = useQuery({
        queryKey: [
            'pricing-evidence',
            scope.principal,
            scope.projectId,
            scope.scenarioId,
            scope.period
        ],
        enabled: !!scope.principal,
        retry: false,
        queryFn: ()=>pricingApiConnect.listEvidence(scope.projectId, scope.scenarioId)
    });
    if (evidence.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading frozen versions…"
    });
    if (evidence.isError) return /*#__PURE__*/ _jsxs(SpecraText, {
        children: [
            "Frozen versions unavailable.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "xs",
                onClick: ()=>evidence.refetch(),
                children: "Retry"
            })
        ]
    });
    const latest = evidence.data.versions.filter((v)=>v.frozen).sort((a, b)=>b.versionNo - a.versionNo)[0];
    return /*#__PURE__*/ _jsx(SpecraText, {
        children: latest ? `v${latest.versionNo} · ${money(latest.totalContractValue)}` : 'Not frozen yet'
    });
}
function Decisions({ scope }) {
    const book = usePricingRateBook(scope);
    if (book.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading decisions…"
    });
    if (book.isError) return /*#__PURE__*/ _jsxs(SpecraText, {
        children: [
            "Decisions unavailable.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "xs",
                onClick: ()=>book.refetch(),
                children: "Retry"
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraText, {
        children: book.data.rows.filter((r)=>!r.removedFromSectionB && r.link?.needsDecision).length
    });
}
export function ScenariosTab({ scope, canEdit }) {
    const navigate = useNavigate();
    const client = useQueryClient();
    const scenarios = usePricingScenarios(scope);
    const [dialog, setDialog] = useState();
    const [name, setName] = useState('');
    const [seed, setSeed] = useState(true);
    const busy = useRef(false);
    const mounted = useRef(true);
    useEffect(()=>{
        mounted.current = true;
        return ()=>{
            mounted.current = false;
        };
    }, []);
    const create = usePricingWrite(()=>pricingApiConnect.createPricing(scope.projectId, {
            scenarioName: name.trim(),
            seedFromExtractions: seed
        }));
    const primary = usePricingWrite((scenarioId)=>projectDataClient.setPrimaryPricingScenario({
            projectId: scope.projectId,
            scenarioId
        }));
    const remove = useMutation({
        mutationKey: [
            'pricing-write'
        ],
        mutationFn: (scenarioId)=>pricingApiConnect.deletePricing(scope.projectId, scenarioId),
        // Refresh the list without refetching metadata for the deleted row.
        onSuccess: ()=>client.invalidateQueries({
                queryKey: [
                    'pricing',
                    scope.principal,
                    scope.projectId
                ]
            })
    });
    const pending = create.isPending || primary.isPending || remove.isPending;
    const failure = create.error || primary.error || remove.error;
    const run = async (action)=>{
        if (busy.current || !canEdit) return;
        busy.current = true;
        try {
            await action();
        } catch  {
        /* shown inline */ } finally{
            busy.current = false;
        }
    };
    const open = (value)=>{
        create.reset();
        primary.reset();
        remove.reset();
        setName('');
        setSeed(true);
        setDialog(value);
    };
    const rows = scenarios.data?.scenarios ?? [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Scenarios",
                action: canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.scenarioNew,
                    disabled: pending,
                    onClick: ()=>open('create'),
                    children: "New scenario"
                }) : undefined
            }),
            !canEdit && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "neutral",
                children: "You can view scenarios. Editing needs write access on this proposal, Growth, or Pricing authority."
            }),
            failure && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Not saved: ",
                    failure.message
                ]
            }),
            scenarios.isError ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Scenarios unavailable: ",
                    scenarios.error.message,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>scenarios.refetch(),
                        children: "Retry"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                testId: T.scenariosTable,
                loading: scenarios.isPending,
                loadingLabel: "Loading pricing scenarios…",
                rows: rows,
                rowKey: (s)=>s.id,
                getRowLabel: (s)=>s.scenarioName,
                emptyTitle: "No pricing scenarios",
                emptyDescription: "Create a scenario to start pricing.",
                columns: [
                    {
                        id: 'name',
                        header: 'Name',
                        cell: (s)=>/*#__PURE__*/ _jsx(SpecraLink, {
                                href: pricingHref(scope.projectId, s.id, 'rates'),
                                renderLink: ({ href, ...props })=>/*#__PURE__*/ _jsx(Link, {
                                        ...props,
                                        to: href
                                    }),
                                children: s.scenarioName
                            })
                    },
                    {
                        id: 'primary',
                        header: 'Primary',
                        cell: (s)=>s.isPrimary ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                children: "Primary"
                            }) : '—'
                    },
                    {
                        id: 'updated',
                        header: 'Updated',
                        cell: (s)=>s.updatedAt && Number.isFinite(Date.parse(s.updatedAt)) ? new Date(s.updatedAt).toLocaleString() : 'Unavailable'
                    },
                    {
                        id: 'frozen',
                        header: 'Last frozen',
                        cell: (s)=>/*#__PURE__*/ _jsx(FrozenVersion, {
                                scope: {
                                    ...scope,
                                    scenarioId: s.id
                                }
                            })
                    },
                    {
                        id: 'decisions',
                        header: 'Rows needing a decision',
                        cell: (s)=>/*#__PURE__*/ _jsx(Decisions, {
                                scope: {
                                    ...scope,
                                    scenarioId: s.id
                                }
                            })
                    }
                ],
                rowActions: canEdit ? (s)=>/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !s.isPrimary && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "xs",
                                "data-testid": T.scenarioPrimary,
                                disabled: pending,
                                onClick: ()=>run(()=>primary.mutateAsync(s.id)),
                                children: "Make primary"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "xs",
                                variant: "destructive",
                                "data-testid": T.scenarioDelete,
                                disabled: pending || s.isPrimary && rows.length > 1,
                                onClick: ()=>open(s),
                                children: "Delete"
                            }),
                            s.isPrimary && rows.length > 1 && /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                children: "Make another scenario primary before deleting this one."
                            })
                        ]
                    }) : undefined
            }),
            dialog && /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: true,
                size: "sm",
                testId: dialog === 'create' ? T.scenarioCreateDialog : T.scenarioDeleteDialog,
                title: dialog === 'create' ? 'New scenario' : `Delete ${dialog.scenarioName}?`,
                onOpenChange: (value)=>{
                    if (!value && !busy.current) setDialog(undefined);
                },
                footer: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            disabled: pending,
                            onClick: ()=>{
                                if (!busy.current) setDialog(undefined);
                            },
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.scenarioConfirm,
                            variant: dialog === 'create' ? 'default' : 'destructive',
                            disabled: !canEdit || pending || dialog === 'create' && !name.trim(),
                            onClick: ()=>run(async ()=>{
                                    if (dialog === 'create') {
                                        const result = await create.mutateAsync();
                                        if (mounted.current) navigate(pricingHref(scope.projectId, result.pricing.id, 'rates'));
                                    } else {
                                        await remove.mutateAsync(dialog.id);
                                        if (mounted.current) setDialog(undefined);
                                    }
                                }),
                            children: pending ? dialog === 'create' ? 'Creating…' : 'Deleting…' : dialog === 'create' ? 'Create scenario' : 'Delete scenario'
                        })
                    ]
                }),
                children: [
                    dialog === 'create' ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Name",
                                htmlFor: T.scenarioName,
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: T.scenarioName,
                                    "data-testid": T.scenarioName,
                                    value: name,
                                    disabled: pending,
                                    onChange: (e)=>setName(e.target.value)
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Start from Section B",
                                htmlFor: T.scenarioSeed,
                                children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                    id: T.scenarioSeed,
                                    "aria-label": "Start from Section B",
                                    "data-testid": T.scenarioSeed,
                                    checked: seed,
                                    disabled: pending,
                                    onCheckedChange: (checked)=>setSeed(checked === true)
                                })
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Frozen versions pinned to a submission keep their copy."
                    }),
                    (create.error || remove.error) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "danger",
                        children: [
                            "Not saved: ",
                            (create.error || remove.error)?.message
                        ]
                    })
                ]
            })
        ]
    });
}
